export interface PaneHeightInput {
  fraction: number;
  collapsed: boolean;
  maximized: boolean;
  /** Bottom pane hosts the x-axis; collapsed height includes label room. */
  hostsXAxis?: boolean;
}

const collapsedPixelHeight = (
  pane: PaneHeightInput,
  collapsedHeight: number,
  xAxisLabelHeight: number
) => (pane.hostsXAxis ? collapsedHeight + xAxisLabelHeight : collapsedHeight);

/**
 * Callers size each pane from this output. Dividers are subtracted
 * first; maximized takes the whole pane area; collapsed uses
 * `collapsedHeight`, plus `xAxisLabelHeight` when that pane also hosts
 * the shared x-axis Expanded panes
 * split leftover space by their drag fractions.
 */
export const getPaneHeights = ({
  containerHeight,
  dividerHeight,
  collapsedHeight,
  xAxisLabelHeight,
  panes,
}: {
  containerHeight: number;
  dividerHeight: number;
  collapsedHeight: number;
  xAxisLabelHeight: number;
  panes: PaneHeightInput[];
}): number[] => {
  if (panes.length === 0 || containerHeight <= 0) {
    return panes.map(() => 0);
  }

  const panesCount = panes.length;
  const totalDividerHeight = Math.max(0, panesCount - 1) * dividerHeight;
  const paneAreaHeight = Math.max(0, containerHeight - totalDividerHeight);

  const maximizedIndex = panes.findIndex(p => p.maximized);

  if (maximizedIndex !== -1) {
    return panes.map((_, i) => (i === maximizedIndex ? paneAreaHeight : 0));
  }

  const collapsedTotal = panes.reduce((sum, pane) => {
    if (!pane.collapsed) {
      return sum;
    }

    return sum + collapsedPixelHeight(pane, collapsedHeight, xAxisLabelHeight);
  }, 0);
  const expandedCount = panes.filter(p => !p.collapsed).length;
  const leftover = Math.max(0, paneAreaHeight - collapsedTotal);

  if (expandedCount === 0) {
    return panes.map(p =>
      p.collapsed
        ? collapsedPixelHeight(p, collapsedHeight, xAxisLabelHeight)
        : 0
    );
  }

  const expandedFractions = panes.map(p =>
    p.collapsed ? 0 : Math.max(p.fraction, 0)
  );
  const sumFractions =
    expandedFractions.reduce((sum, next) => sum + next, 0) || expandedCount;

  return panes.map((pane, i) => {
    if (pane.collapsed) {
      return collapsedPixelHeight(pane, collapsedHeight, xAxisLabelHeight);
    }

    return leftover * (expandedFractions[i] / sumFractions);
  });
};
